import { Suspense } from 'react';
import type { FC } from 'react';
import { IntlProvider } from 'react-intl';
import { QueryClientProvider } from 'react-query';
import type { WidgetBackgroundVariant } from '@onramp/components/AppThemeProvider';
import { AppThemeProvider } from '@onramp/components/AppThemeProvider';
import { ExperimentationProvider } from '@onramp/components/ExperimentationProvider';
import { UnauthedGraphqlProvider } from '@onramp/components/GraphqlProviders/UnauthedGraphqlProvider';
import KillSwitchGuard from '@onramp/components/KillSwitchGuard';
import { GenericLoadingScreen } from '@onramp/components/screens/GenericLoadingScreen';
import type { InitParamsType, InternalInitParamsType } from '@onramp/types/initParams';
import { queryClient } from '@onramp/utils/reactQueryClient';
import { useThemeSpectrum } from '@onramp/utils/useThemeSpectrum';
import { ErrorBoundary } from '@onramp/v2/client/components/errors/ErrorBoundary';
import { InitParamsProvider } from '@onramp/v2/client/contexts/initParams/InitParamsContext';
import { useRouter } from 'next/router';
import type { MessagesMap } from '@cbhq/intl';

type I18nProviderProps = {
  locale: string;
  messages: MessagesMap;
};

export const LandingProvidersV2: FC<
  I18nProviderProps & {
    initParamsFromServer?: InitParamsType;
    internalInitParamsFromServer?: InternalInitParamsType;
    shouldSuspend?: boolean;
    widgetBackgroundVariant?: WidgetBackgroundVariant;
  }
> = ({
  children,
  initParamsFromServer,
  internalInitParamsFromServer,
  locale,
  messages,
  shouldSuspend = true,
  widgetBackgroundVariant = 'solid',
}) => {
  const spectrum = useThemeSpectrum();
  const params = initParamsFromServer ?? {
    appId: '',
    destinationWallets: [],
  };
  const DEFAULT_LOCALE = useRouter().locale;

  return (
    <AppThemeProvider spectrum={spectrum} widgetBackgroundVariant={widgetBackgroundVariant}>
      <IntlProvider locale={locale} defaultLocale={DEFAULT_LOCALE} messages={messages}>
        <ErrorBoundary>
          <QueryClientProvider client={queryClient}>
            <UnauthedGraphqlProvider>
              <ExperimentationProvider>
                <InitParamsProvider
                  initParamsFromServer={params}
                  internalInitParamsFromServer={internalInitParamsFromServer}
                >
                  <KillSwitchGuard>
                    {shouldSuspend ? (
                      <Suspense fallback={<GenericLoadingScreen />}>{children}</Suspense>
                    ) : (
                      children
                    )}
                  </KillSwitchGuard>
                </InitParamsProvider>
              </ExperimentationProvider>
            </UnauthedGraphqlProvider>
          </QueryClientProvider>
        </ErrorBoundary>
      </IntlProvider>
    </AppThemeProvider>
  );
};

type GuestCheckoutProvidersProps = I18nProviderProps & {
  initParamsFromServer: InitParamsType | undefined;
  internalInitParamsFromServer: InternalInitParamsType | undefined;
};

export const GuestCheckoutProviders: FC<GuestCheckoutProvidersProps> = ({
  children,
  initParamsFromServer,
  internalInitParamsFromServer,
  locale,
  messages,
}) => {
  const spectrum = useThemeSpectrum();
  const params = initParamsFromServer ?? {
    appId: '',
    destinationWallets: [],
  };
  const DEFAULT_LOCALE = useRouter().locale;

  return (
    <AppThemeProvider spectrum={spectrum} widgetBackgroundVariant="solid">
      <IntlProvider locale={locale} defaultLocale={DEFAULT_LOCALE} messages={messages}>
        <ErrorBoundary>
          <QueryClientProvider client={queryClient}>
            <UnauthedGraphqlProvider>
              <ExperimentationProvider>
                <InitParamsProvider
                  initParamsFromServer={params}
                  internalInitParamsFromServer={internalInitParamsFromServer}
                >
                  <KillSwitchGuard>
                    <Suspense fallback={<GenericLoadingScreen />}>{children}</Suspense>
                  </KillSwitchGuard>
                </InitParamsProvider>
              </ExperimentationProvider>
            </UnauthedGraphqlProvider>
          </QueryClientProvider>
        </ErrorBoundary>
      </IntlProvider>
    </AppThemeProvider>
  );
};
